import React, {useEffect} from 'react'
import {routeKeys, legacyRoutes, routeHelper} from './routeKeys'
import {roles} from '../ag-common/tools/roles'
import AusbildungsnachweiseOverview from '../pages/ausbildungsnachweiseOverview/index'
import AlteAusbildungsnachweiseOverview from '../pages/alteAusbildungsnachweiseOverview/alteAusbildungsnachweiseOverview'
import NachweisDetailCreateContainer from '#components/o/NachweisDetail/NachweisDetailCreateContainer'
import NachweisDetailEditContainer from '#components/o/NachweisDetail/NachweisDetailEditContainer'
import Empty from '../pages/empty'
import {AusbildungsplanContainer} from '#components/o/Ausbildungsplan/AusbildungsplanContainer'
import Versetzungsplan from '../pages/versetzungsplan/Versetzungsplan'
import Ressourcenplanung from '../pages/ressourcenplanung/Ressourcenplanung'
import PraxisaufgabenOverview from '../pages/praxisaufgabenOverview/PraxisaufgabenOverview'
import {PraxisaufgabenDetail} from '../pages/praxisaufgabenDetail/container'
import {Wissensportal} from '../pages/wissensportal/container'
import MeineAzubis from '../pages/meineazubis/container'
import MeineAusbilder from '../pages/meineausbilder'
import MeineAusbildung from '../pages/meineausbildung/container'
import Uebersicht from '../pages/uebersicht/container'
import UebersichtAusbildungsnachweise from '../pages/uebersicht/UebersichtAusbildungsnachweise'
import UebersichtAusbildungsplan from '../pages/uebersicht/UebersichtAusbildungsplan'
import UebersichtPraxisaufgaben from '../pages/uebersicht/UebersichtPraxisaufgaben'
import Chat from '../pages/chat/Chat'
import Feedback from '../pages/feedback/Feedback'
import AllNotifications from '../pages/allNotifications/container'
import LicensesPage from '../pages/licenses/web'
import LicensesPageMobile from '../pages/licenses/mobile'
import {Navigate, Route, Routes, useLocation, useNavigate, useParams} from 'react-router-dom'
import PrivateRoute from '#components/PrivateRoute'
import {usePermissions} from '#hooks/usePermissions'
import {DEFAULT_PATH_NO_ROLE, defaultPathForRole} from '#helpers/defaultPathForRole'
import useCurrentUser from '#hooks/store/useCurrentUser'
import LoginRedirect from '../pages/login/loginRedirect'
import LoginError from '../pages/login/loginError'
import Loading from '#components/a/Loading'

export const AzubiGuideRoutes = ({wrapper}) => {
  const getDefaultPath = (role, permissions) => {
    const {pathname} = useLocation()
    if (permissions?.meineAusbilder?.manage && pathname === '/meineorganisation') {
      return '/meineorganisation/meineazubis'
    }
    return defaultPathForRole(role)
  }
  const {role, loginError} = useCurrentUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (loginError) {
      navigate(DEFAULT_PATH_NO_ROLE)
    }
  }, [loginError])

  const permissions = usePermissions()
  const defaultPath = getDefaultPath(role, permissions)

  return (
    <Routes>
      <Route element={wrapper}>
        <Route path={'/login-redirect'} exact element={<LoginRedirect />} />
        <Route path={'/lizenzen'} exact element={<LicensesPage />} />
        <Route path={'/lizenzen-mobile'} exact element={<LicensesPageMobile />} />
        <Route path={DEFAULT_PATH_NO_ROLE} exact element={<LoginError />} />
        {role &&
          privateRoutesConfig.map((routeConfig, i) => (
            <Route
              path={routeConfig.path}
              exact
              key={i}
              element={
                <PrivateRoute path={routeConfig.path} requiredRole={routeConfig.role} currentUserRole={role}>
                  {routeConfig.component}
                </PrivateRoute>
              }
            />
          ))}
        {role && <Route path="*" element={<Navigate to={defaultPath} />} />}
        {legacyRoutes.map((legacyRoute, i) => (
          <Route
            path={legacyRoute.legacy}
            exact
            key={i + privateRoutesConfig.length}
            element={<LegacyRedirect redirect={legacyRoute.redirect} />}
          />
        ))}
        <Route path={'/*'} element={<LoginRedirect />} />
      </Route>
    </Routes>
  )
}

export const LegacyRedirect = ({redirect}) => {
  const navigate = useNavigate()
  const {azubiId} = useParams()
  if (redirect === routeKeys.nextPerUser) {
    redirect = routeHelper.nextPerUser(azubiId)
  }
  useEffect(() => {
    navigate(redirect)
  })
  return <Loading />
}

const privateRoutesConfig = [
  {
    path: routeKeys.ausbildungsnachweise,
    component: <AusbildungsnachweiseOverview />,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.alteAusbildungsnachweise,
    component: <AlteAusbildungsnachweiseOverview />,
    role: [roles.AZUBI],
  },
  {
    path: routeKeys.newAusbildungsnachweisWithNummer,
    component: <NachweisDetailCreateContainer />,
    role: [roles.AZUBI],
  },
  {
    path: routeKeys.ausbildungsnachweisePerUser,
    component: <AusbildungsnachweiseOverview />,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ausbildungsnachweisDetail,
    component: <NachweisDetailEditContainer />,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ausbildungsplan,
    component: <Empty />,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ausbildungsplanForUser,
    component: <Empty />,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ausbildungsplanChapterStatic,
    component: <AusbildungsplanContainer />,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ausbildungsplanChapterQuestions,
    component: <AusbildungsplanContainer />,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.ablaufundtermine,
    component: <Versetzungsplan />,
    isPrivate: true,
    role: [roles.AZUBI, roles.AUSBILDER, roles.TRAINER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.ressourcenplanung,
    component: <Ressourcenplanung />,
    isPrivate: true,
    role: [roles.AUSBILDER],
  },
  {
    path: routeKeys.praxisaufgabenOverview,
    component: <PraxisaufgabenOverview />,
    isPrivate: true,
    role: [roles.AZUBI, roles.AUSBILDER, roles.TRAINER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.praxisaufgabenDetail,
    component: <PraxisaufgabenDetail />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.next,
    component: <Wissensportal />,
    isPrivate: true,
    role: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.nextPerUser,
    component: <Wissensportal />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.meineazubisForAusbilder,
    component: <MeineAzubis />,
    isPrivate: true,
    role: [roles.AUSBILDER],
  },
  {
    path: routeKeys.meineausbilder,
    component: <MeineAusbilder />,
    isPrivate: true,
    role: [roles.AUSBILDER],
  },
  {
    path: routeKeys.meineazubis,
    component: <MeineAzubis />,
    isPrivate: true,
    role: [roles.TRAINER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.meineausbildung,
    component: <MeineAusbildung />,
    isPrivate: true,
    role: [roles.AZUBI],
  },
  {
    path: routeKeys.uebersicht,
    component: <Uebersicht />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.uebersichtAusbildungsnachweise,
    component: <UebersichtAusbildungsnachweise />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.uebersichtAusbildungsplan,
    component: <UebersichtAusbildungsplan />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.uebersichtPraxisaufgaben,
    component: <UebersichtPraxisaufgaben />,
    isPrivate: true,
    role: [roles.AUSBILDER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.chat,
    component: <Chat />,
    isPrivate: true,
    roles: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER, roles.TRAINER],
  },
  {
    path: routeKeys.feedback,
    component: <Feedback />,
    isPrivate: true,
    roles: [roles.AZUBI, roles.AUSBILDER, roles.FACHAUSBILDER],
  },
  {
    path: routeKeys.benachrichtigungen,
    component: <AllNotifications />,
    isPrivate: true,
  },
]
